import React, { useState } from "react";
import CommonButtons from "src/shared/components/CommonButtons";
import { LABELS } from "./OrderSummaryConstants";
import { ModalWrapper } from "src/shared/components/ModalWrapper";

interface OrderSummaryActionsProps {
  handlePrint: () => void;
  cancelOrder: (processRefund: boolean) => void;
  isCancelDisabled: boolean;
  orderStatus?: string;
  showRefundOption?: boolean;
  receivedAmount?: number;
}

const OrderSummaryActions: React.FC<OrderSummaryActionsProps> = ({ handlePrint, cancelOrder, isCancelDisabled, orderStatus, showRefundOption = false, receivedAmount = 0 }) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [processRefund, setProcessRefund] = useState(false);
  const isDelivered = orderStatus === "Delivered";
  const isCancelled = orderStatus === "Cancelled";

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
    setProcessRefund(false);
  };

  const handleConfirmCancel = () => {
    cancelOrder(processRefund);
    setShowCancelConfirm(false);
    setProcessRefund(false);
  };

  const handleCloseModal = () => {
    setShowCancelConfirm(false);
    setProcessRefund(false);
  };

  const handleRefundToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProcessRefund(e.target.checked);
  };

  // Logic for confirm button state:
  const isConfirmDisabled = showRefundOption ? !processRefund : isCancelDisabled;

  // If order is cancelled, show only cancellation message
  if (isCancelled) {
    return (
      <div className="flex flex-col gap-3">
        <div className="text-center py-3 bg-gray-100 rounded border border-gray-300">
          <p className="text-gray-600 font-medium">Order has been cancelled</p>
          {receivedAmount > 0 && <p className="text-sm text-green-600 mt-1">Refund processed: {receivedAmount} QAR</p>}
        </div>
      </div>
    );
  }

  // If order is delivered, show print button
  if (isDelivered) {
    return (
      <div className="flex flex-col gap-3">
        <CommonButtons label={LABELS.PRINT_RECEIPT} onClick={handlePrint} variant="primary" className="w-full" />
      </div>
    );
  }

  // For active orders (not delivered, not cancelled), show both buttons
  return (
    <>
      <div className="flex flex-col gap-3">
        <CommonButtons label={LABELS.PRINT_RECEIPT} onClick={handlePrint} variant="primary" className="w-full" />
        <CommonButtons label={LABELS.CANCEL} onClick={handleCancelClick} variant="secondary" disabled={isCancelDisabled} className="w-full border-red-600 text-red-600 hover:bg-red-50" />
      </div>

      {/* Cancel Confirmation Modal with Refund Option */}
      {showCancelConfirm && (
        <ModalWrapper title="Cancel Order" onClose={handleCloseModal} showCloseButton={true} width="max-w-md">
          <div className="text-center p-4">
            <div className="mb-4">
              <p className="text-gray-700 text-lg font-medium">Are you sure you want to cancel this order?</p>
              <p className="text-gray-500 text-sm mt-2">This action cannot be undone.</p>
            </div>

            {/* Refund Option - Only show if there was payment received */}
            {showRefundOption && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={processRefund} onChange={handleRefundToggle} className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500" />
                  <span className="text-sm text-gray-700 font-medium">Process refund for received payments</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 text-left ml-7">
                  Refund amount: <strong>{receivedAmount} QAR</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1 text-left ml-7">You must check this box to confirm cancellation and process refund.</p>
              </div>
            )}

            {/* Show different message based on whether refund option is available */}
            {!showRefundOption && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">No payments were received for this order. Cancellation will not process any refunds.</p>
              </div>
            )}

            <div className="flex gap-3 justify-center pt-2">
              <button onClick={handleCloseModal} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Keep Order
              </button>
              <button
                onClick={handleConfirmCancel}
                className={`px-6 py-2 rounded-md transition-colors ${isConfirmDisabled ? "bg-gray-400 text-gray-200 cursor-not-allowed" : "bg-red-600 text-white hover:bg-red-700"}`}
                disabled={isConfirmDisabled}
              >
                {showRefundOption && processRefund ? "Cancel & Refund" : "Confirm Cancel"}
              </button>
            </div>

            {/* Helper text for refund case */}
            {showRefundOption && !processRefund && <p className="text-xs text-red-500 mt-2">Please check the refund box to enable cancellation</p>}
          </div>
        </ModalWrapper>
      )}
    </>
  );
};

export default OrderSummaryActions;
